/**
 * Shared pen-trace + chalk-dust reveal primitives, used by both the generative
 * external textpaths (TapestryExternal) and the curated layout text (CuratedText).
 * Each call site owns its own rAF/progress loop and supplies pen positions; this
 * module provides the constants, the particle model, and the dust opacity curve.
 */

// Time to trace one line of text — ~1.21× the original for a slow, deliberate
// reveal. The dust constants below are scaled by the same factor so the whole
// pen + chalk-dust reveal plays uniformly slower without changing its character.
export const LINE_DURATION_MS = 811;

// Pen tip offset within pen.jpg (normalized) — the point rotations pivot around
// so the tip stays on the text, and the pen render size relative to line height.
export const PEN_TIP_X = 0.36;
export const PEN_TIP_Y = 0.82;
export const PEN_SIZE_SCALE = 2.2;

// Hard outline drawn around the white pen sprite so it stays visible on light
// curated backgrounds. Tinted to the dark generative background colour: under
// the generative canvas's 'screen' blend a near-background outline contributes
// nothing, so it only reads on the lighter curated backgrounds (normal blend).
export const PEN_OUTLINE_COLOR = '#0a2445';

// Small oscillating tilt added to the pen's tangent angle so it rocks back and
// forth as if forming letters while it writes. `phase` is measured in lines
// written (one unit = one full line), so the rock rate is consistent regardless
// of line length.
export const PEN_ROCK_AMPLITUDE_DEG = 7;
export const PEN_ROCK_CYCLES = 1.5; // back-and-forth cycles per line
export function penRockOffset(phase) {
  return Math.sin(phase * PEN_ROCK_CYCLES * 2 * Math.PI) * PEN_ROCK_AMPLITUDE_DEG;
}

// Chalk-dust particles spawned along the pen's path as it writes.
export const DUST_VARIANTS = [
  '/tapestry/chalk-burst.jpg',
  '/tapestry/chalk-wisp.jpg',
  '/tapestry/chalk-scatter-1.jpg',
  '/tapestry/chalk-scatter-2.jpg',
];
export const DUST_SPAWN_INTERVAL_MS = 133; // base min gap between spawns (jittered); 110 × 1.21
export const DUST_LIFETIME_MS = 1271; // 1050 × 1.21, in step with the twice-slowed trace
export const DUST_MAX_OPACITY = 0.3; // half the original 0.6 — chalk lightened 50%
const DUST_SIZE_MIN_SCALE = 1.4;
const DUST_SIZE_MAX_SCALE = 2.4;
const DUST_POSITION_JITTER = 0.35; // fraction of lineHeight

/** Opacity envelope: fast fade-in, brief hold, slow fade-out. */
export function dustEnvelope(age) {
  if (age < 0 || age > 1) return 0;
  if (age < 0.12) return age / 0.12;
  if (age < 0.32) return 1;
  return Math.max(0, 1 - (age - 0.32) / 0.68);
}

/** A chalk puff at (x, y), jittered around the pen, sized off the line height. */
export function spawnDustParticle(id, time, x, y, lineHeight) {
  const jitter = lineHeight * DUST_POSITION_JITTER;
  const sizeScale = DUST_SIZE_MIN_SCALE + Math.random() * (DUST_SIZE_MAX_SCALE - DUST_SIZE_MIN_SCALE);
  return {
    id,
    x: x + (Math.random() - 0.5) * jitter,
    y: y + (Math.random() - 0.5) * jitter * 0.7,
    variantIdx: Math.floor(Math.random() * DUST_VARIANTS.length),
    rotation: Math.random() * 360,
    baseSize: lineHeight * sizeScale,
    spawnedAt: time,
    age: 0,
  };
}

/** Age existing particles (dropping expired ones) and append a new one if any. */
export function advanceParticles(particles, time, newParticle) {
  const out = [];
  for (const pt of particles) {
    const age = (time - pt.spawnedAt) / DUST_LIFETIME_MS;
    if (age < 1) out.push({ ...pt, age });
  }
  if (newParticle) out.push(newParticle);
  return out;
}
